import ClassMode from 'enums/ClassMode';
import logger from 'logger';
import { call, put, takeEvery, takeLatest, select, retry } from 'redux-saga/effects';
import * as chimeActions from 'store/chime/chimeActions';
import { joinMeetingFlowSaga } from 'store/classroom/classroomFlowSagas';
import * as conferenceActions from 'store/conference/conferenceActions';
import * as userActions from 'store/user/userActions';
import {
  createClassMeeting,
  createConferenceSession,
  createUser,
  getAllQuestions,
  getConferenceSession,
  hasMeetingBeenCreated,
  updateConferenceSessionUser,
} from 'utils/apiCalls';
import { authorizeToken } from './userSagas';

export function* guestLoginFlowSaga(action) {
  const { name, push, newPath } = action.payload;
  const user = yield call(createUser, name, '', ClassMode.Guest, false, false, '');
  yield put(
    userActions.setUserAction({
      firstName: user.FirstName,
      lastName: user.LastName,
      email: user.Email,
      moodle: user.MoodleUser,
      classMode: user.Type,
      userId: user.UserId,
      admin: user.Admin,
    })
  );
  push(newPath);
}

export function* initializeConferenceTokenFlowSaga(action) {
  const tries = 2;
  const retryDelay = 2000;

  try {
    yield put(conferenceActions.initAction({ key: 'initializeConferenceToken' }));

    const { token, audioRef } = action.payload;
    logger.debug('Token value passed into initializeConferenceTokenFlowSaga:', { token });
    const { user, newToken, tokenData } = yield retry(tries, retryDelay, authorizeToken, token);

    localStorage.setItem('conference-token', btoa(newToken));

    const questions = yield retry(
      tries,
      retryDelay,
      getAllQuestions,
      tokenData.sessionId,
      [],
      true,
      user.UserId
    );
    logger.debug('successfully initialized questions', { questions });
    if (questions && questions.length) {
      yield put(conferenceActions.setAllQuestionsAction({ questions }));
      logger.debug('successfully set questions', { questions });
    }

    yield put(conferenceActions.getConferenceAction({ sessionId: tokenData.sessionId }));

    if (['presenter', 'admin'].includes(tokenData.userRole)) {
      const meetingCreated = yield retry(
        tries,
        retryDelay,
        hasMeetingBeenCreated,
        tokenData.sessionId
      );
      let externalMeeting = meetingCreated?.meeting;
      if (!meetingCreated?.isCreated) {
        externalMeeting = (yield retry(
          tries,
          retryDelay,
          createClassMeeting,
          tokenData.sessionId,
          user.UserId,
          tokenData.sessionName
        )).meeting;
      }
      yield put(
        chimeActions.setExternalMeetingAction({
          externalMeetingName: externalMeeting?.MeetingName || '',
          externalMeetingId: externalMeeting?.MeetingId || '',
        })
      );
      yield retry(tries, retryDelay, joinMeetingFlowSaga, {
        payload: {
          externalMeetingId: tokenData.sessionId,
          // externalMeetingName: tokenData.sessionName,
          externalUserId: user.UserId,
          audioRef,
        },
      });
    }

    yield retry(
      tries,
      retryDelay,
      updateConferenceSessionUser,
      user.UserId,
      tokenData.sessionId,
      'ENTER'
    );
    window.addEventListener('unload', async () => {
      updateConferenceSessionUser(user.UserId, tokenData.sessionId, 'EXIT');
      window.removeEventListener('unload');
    });

    yield put(userActions.successAction({ key: 'initializeConferenceToken' }));
  } catch (error) {
    yield put(userActions.errorAction({ key: 'initializeConferenceToken', error }));

    const { push } = yield select(({ utils }) => ({
      push: utils.push,
    }));
    push('/error/failedinit');
  }
}

export function* authorizeTokenFlowSaga(action) {
  try {
    yield put(userActions.initAction({ key: 'authorizeToken' }));
    const { token } = action.payload;
    const { tokenData } = yield call(authorizeToken, token);

    let conferenceData = yield call(getConferenceSession, tokenData.sessionId);

    if (conferenceData.error) {
      throw new Error('Get Conference Failed: ', conferenceData.error);
    } else if (tokenData.userRole !== 'admin') {
      throw new Error(' Not allowed');
    } else if (!conferenceData.data) {
      logger.info('Creating Conference Resources');
      const newConf = yield call(
        createConferenceSession,
        tokenData.sessionId,
        tokenData.sessionName
      );
      // TODO check for create conference failure
      conferenceData = newConf;
      yield put(
        conferenceActions.setupBroadcastSessionAction({
          name: tokenData.sessionName,
          solutionId: tokenData.sessionId,
        })
      );
    }
    yield put(conferenceActions.setConferenceAction(conferenceData.data));
    yield put(userActions.successAction({ key: 'authorizeToken' }));
  } catch (error) {
    yield put(userActions.errorAction({ key: 'authorizeToken', error }));
  }
}

export function* conferenceTokenAuthFlowSaga(action) {
  try {
    yield put(userActions.initAction({ key: 'conferenceTokenAuth' }));
    const { token } = action.payload;
    const { tokenData } = yield call(authorizeToken, token);

    let conferenceData = yield call(getConferenceSession, tokenData.sessionId);

    if (conferenceData.error) {
      throw new Error('Get Conference Failed: ', conferenceData.error);
    }
    yield put(conferenceActions.setConferenceAction(conferenceData.data));
    yield put(userActions.successAction({ key: 'conferenceTokenAuth' }));
  } catch (error) {
    yield put(userActions.errorAction({ key: 'conferenceTokenAuth', error }));
  }
}

export default function* userWatcher() {
  yield takeLatest(userActions.guestLoginAction().type, guestLoginFlowSaga);
  yield takeEvery(
    userActions.initializeConferenceTokenAction().type,
    initializeConferenceTokenFlowSaga
  );
  yield takeEvery(userActions.authorizeTokenAction().type, authorizeTokenFlowSaga);
  yield takeEvery(userActions.conferenceTokenAuthAction().type, conferenceTokenAuthFlowSaga);
}
